import { useEffect, useMemo, useState } from 'react';
import defaultDates from '../../../utils/defaultDates';
import getPersonaName from '../../../helpers/getPersonaName';
import useHierarchyData from '../../../hooks/useHierarchyData';
import toMillis from '../../../helpers/dataHelpers/matrixDataHelpers/toMillis';
import countParticipantsPerPersona from '../../../helpers/personas/countParticipantsPerPersona';
import MultipleMomentBarChart from '../../Visualisations/moment/MultipleMomentBarChart';
import ChartDataTable from '../ChartDataTable';
import { multipleChoiceTable } from '../../../helpers/dataHelpers/chartDataTable';
import usePersonaTerms from '../../../hooks/usePersonaTerms';
import { buildTemplateEventResult } from './templateEventRecords';
import useChartColors from '../../../hooks/useChartColors';

// AI-CONTEXT: See docs/features/api-data-fetching.md
//
// Telt per deelnemer bij hoeveel outputregistraties die gekoppeld is. Leest
// `Participants` op de outputregels zelf — de opvolger van het losse
// deelnemersveld, waar ParticipantsData nog op draait voor oude formulieren.

/**
 * @param {object} props
 * @param {import("../../../types/Firebase").EventFields} props.field - Het outputs-veld van het formulier
 * @param {string} [props.color] - Kleur van de balken
 * @param {string} [props.startDate] - Begin van de periode
 * @param {string} [props.endDate] - Einde van de periode
 * @param {object|null} [props.templateActivityData] - Vooraf opgehaalde sjabloondata
 * @returns {JSX.Element|null}
 */
const OutputParticipantsData = ({ field, color, startDate, endDate, templateActivityData }) => {
  const isTemplate = !!templateActivityData;
  const [chartData, setChartData] = useState([]);

  const personaTerms = usePersonaTerms();

  const { defaultStartDate: dsRaw, defaultEndDate: deRaw } = useMemo(() => defaultDates(), []);
  const dsMs = useMemo(() => toMillis(dsRaw) ?? 0, [dsRaw]);
  const deMs = useMemo(() => toMillis(deRaw) ?? 8640000000000000, [deRaw]);
  const startMs = useMemo(() => toMillis(startDate) ?? dsMs, [startDate, dsMs]);
  const endMs = useMemo(() => toMillis(endDate) ?? deMs, [endDate, deMs]);

  const { palette: PALETTE } = useChartColors();

  const live = useHierarchyData({
    syncType: 'Event',
    id: isTemplate ? '' : field.ID,
    startDate: startMs,
    endDate: endMs,
  });

  const template = useMemo(
    () => buildTemplateEventResult(templateActivityData, field.ID),
    [templateActivityData, field.ID]
  );

  // In één useMemo, zodat de afgeleide telling niet elke render een nieuwe
  // array-referentie krijgt.
  // AI-CONTEXT: See docs/pitfalls.md#state-useeffect-inline-filter
  const liveRecords = live.records;
  const templateRecords = template?.records;
  const counts = useMemo(
    () => countParticipantsPerPersona(isTemplate ? (templateRecords || []) : liveRecords),
    [isTemplate, templateRecords, liveRecords]
  );

  const projectHierarchy = isTemplate ? (template?.hierarchy || {}) : live.hierarchy;
  const loading = isTemplate ? false : live.loading;

  // Namen van deelnemers ophalen zodra de tellingen er zijn
  useEffect(() => {
    if (loading || counts.length === 0) {
      setChartData([]);
      return;
    }

    let cancelled = false;

    const resolve = async () => {
      const responses = await Promise.all(
        counts.map(async ({ personaId, count }, index) => {
          let name;
          try { name = await getPersonaName(personaId); } catch { name = String(personaId); }
          return { name, count, color: PALETTE[index % PALETTE.length] };
        })
      );

      if (!cancelled) {
        setChartData([{ moment: 'Totaal', responses, others: [] }]);
      }
    };

    resolve();
    return () => { cancelled = true; };
  }, [counts, loading, PALETTE]);

  // Geen gekoppelde deelnemers? Dan ook geen lege grafiek tonen.
  if (!loading && counts.length === 0) return null;

  return (
    <>
      <div className='home-kpi-question-title-container'>
        <p className='event-field-title'>{personaTerms.plural}</p>
      </div>

      <MultipleMomentBarChart
        data={chartData}
        projectAverages={{}}
        totalResponses={counts.length}
        projectHierarchy={projectHierarchy}
        color={color}
        source="event"
      />
      <ChartDataTable table={multipleChoiceTable(chartData)} />
    </>
  );
};

export default OutputParticipantsData;
